import type { SelectOptions } from "@/components/DetailsCard/DetailsCard";
import EmptyState from "@/components/EmptyState";
import ListItem from "@/components/ListItem";
import Progress from "@/components/Progress";
import SubSectionHeading from "@/components/SubSectionHeading";
import CareerPlanSummaryCard from "./_CareerPlanSummaryCard";
import {
  getExperienceIndexCompletion,
  getExperienceIndexHubState,
  type ExperienceIndexHubState
} from "./careerPlanHubSummaries";
import type { CareerPlanAssessment, CareerPlanCompetencyScore } from "./careerPlanHistory";
import { getExperienceIndexCompetencyIcon } from "./experienceIndexIcons";
import {
  formatExperienceIndexHubScore,
  getExperienceIndexCategoryCompletions,
  getHubAreasToDiscuss,
  getHubTopStrengths,
  type ExperienceIndexHubCompetency
} from "./experienceIndexResults";

function HighlightNames({
  heading,
  highlights,
  testId
}: {
  heading: string;
  highlights: ExperienceIndexHubCompetency[];
  testId: string;
}) {
  if (highlights.length === 0) return null;

  return (
    <div className="flex flex-col" data-testid={testId}>
      <SubSectionHeading>{heading}</SubSectionHeading>
      {highlights.map((highlight) => (
        <div key={highlight.id} className="flex items-start">
          <ListItem
            iconName={getExperienceIndexCompetencyIcon(highlight.label)}
            headline={highlight.label}
            supportingText={formatExperienceIndexHubScore(highlight.score)}
            iconBackground
          />
        </div>
      ))}
    </div>
  );
}

function experienceIndexOptions({
  hubState,
  scores
}: {
  hubState: ExperienceIndexHubState;
  scores: CareerPlanCompetencyScore[];
}): SelectOptions[] {
  switch (hubState) {
    case "never_started":
      return [];
    case "unfinished":
      return getExperienceIndexCategoryCompletions(scores).map((category) => ({
        iconName: category.iconName,
        iconBackground: true,
        headline: <span data-testid={`career-plan-ei-category-${category.id}`}>{category.label}</span>,
        trailingSupportingText: `${category.percent ?? 0}%`,
        supportingText: (
          <span aria-hidden="true">
            <Progress variant="linear" tone="primary" value={(category.percent ?? 0) / 100} className="w-full" />
          </span>
        )
      }));
    case "finished":
      return [];
    default: {
      const _exhaustive: never = hubState;
      return _exhaustive;
    }
  }
}

export default function ExperienceIndexSummaryCard({
  assessment,
  loading,
  pgaId
}: {
  assessment: CareerPlanAssessment | null;
  loading: boolean;
  pgaId?: string;
}) {
  const completion = getExperienceIndexCompletion(assessment);
  const hubState = getExperienceIndexHubState(completion);
  const scores = assessment?.scores ?? [];
  const strengths = getHubTopStrengths(scores);
  const areasToDiscuss = getHubAreasToDiscuss(scores);

  return (
    <CareerPlanSummaryCard
      title="Experience Index"
      testId="career-plan-ei"
      updatedAt={hubState === "never_started" ? undefined : assessment?.updatedAt}
      loading={loading}
      pgaId={pgaId}
      viewAllTab={hubState === "finished" ? "results" : "answers"}
      showViewAll={hubState !== "never_started"}
      options={experienceIndexOptions({ hubState, scores })}
    >
      {hubState === "never_started" ? (
        <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center">
          <EmptyState
            iconName="quiz"
            title="No Experience Index"
            caption="This person has not answered Experience Index questions yet."
          />
        </div>
      ) : hubState === "finished" && strengths.length === 0 && areasToDiscuss.length === 0 ? (
        <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center">
          <EmptyState
            iconName="quiz"
            title="No Experience Index Results"
            caption="Experience Index results are not available yet."
          />
        </div>
      ) : hubState === "finished" ? (
        <div className="flex flex-col gap-6 pb-4" data-testid="career-plan-ei-highlights">
          <HighlightNames heading="Top Strengths" highlights={strengths} testId="career-plan-ei-strengths" />
          <HighlightNames heading="Areas to Discuss" highlights={areasToDiscuss} testId="career-plan-ei-gaps" />
        </div>
      ) : null}
    </CareerPlanSummaryCard>
  );
}
